// src/encounter/lib/to-chief-complaint.ts
// Wire mappers for the pre-encounter chief-complaint quick-capture (PROD-1588, Samer
// live-review verdict 2026-07-23). Shapes traced from Scrips.Patient this session:
// PatientComplaint DTO (Application/Dtos/PatientChart/PatientComplaint.cs) + the
// replace-all CreatePatientComplaintCommand handler + GetPatientComplaints projection.
//
// The DS ChiefComplaintEditor works in DISPLAY STRINGS (selection chips), so the page keeps
// a side-map display → category (from the RAG suggestion rows) and these mappers convert
// between the chip list and the wire document. Because the POST is a replace-all upsert,
// `buildPatientComplaint` round-trips kept complaints VERBATIM (incl. their patientHpi
// children — dropping them would erase in-encounter HPI answers stored under the complaint)
// and only appends/removes entries. `templateItem` is a read-side RAG enhancement the write
// handler ignores — stripped to keep the POST slim.

import type { PatientHpiEntry, TemplateItem } from '@/types/encounter-template-api';

/** Complaint.Type values (Flutter app_constants.dart:210-211; BE PatientComplaintCategory). */
export const CHIEF_COMPLAINT_TYPE = 'ChiefComplaint';
export const ASSOCIATED_COMPLAINT_TYPE = 'AssociatedChiefComplaint';

export interface ComplaintCoding {
  code: string | null;
  display: string | null;
  system: string | null;
}

export interface ComplaintEntry {
  type: string | null;
  coding: ComplaintCoding | null;
  complaintCategory: string | null;
  /** In-encounter HPI answers stored under this complaint — round-tripped verbatim, and
   *  EDITED by the chief-complaint HPI block (PROD-2136, lib/chief-complaint-hpi.ts). */
  patientHpi?: PatientHpiEntry[] | null;
  /** Read-side template sections, server-resolved by `complaintCategory` — what the HPI block
   *  renders as chip groups (PROD-2136). The write handler ignores it; stripped on POST. */
  templateItem?: TemplateItem[] | null;
}

export interface PatientComplaintModel {
  encounter: string;
  subject: string;
  comment?: string | null;
  notePlainText?: string | null;
  complaints: ComplaintEntry[];
}

/** True for the complaint categories the quick-capture card owns (not GlobalNotes). */
export function isComplaintEntry(entry: ComplaintEntry): boolean {
  return (
    entry.type === CHIEF_COMPLAINT_TYPE ||
    entry.type === ASSOCIATED_COMPLAINT_TYPE ||
    // BE enum spells the associated category without the "Chief" (PatientComplaintCategory
    // .AssociatedComplaint) — accept both so a row written by either vocabulary hydrates.
    entry.type === 'AssociatedComplaint'
  );
}

/** The selected chip list — complaint display names, order-preserving, de-duplicated. */
export function complaintsToSelected(model: PatientComplaintModel | undefined): string[] {
  if (!model?.complaints) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of model.complaints) {
    if (!isComplaintEntry(entry)) continue;
    const display = entry.coding?.display?.trim();
    if (!display || seen.has(display)) continue;
    seen.add(display);
    out.push(display);
  }
  return out;
}

/** display → complaintCategory for every persisted complaint (the HPI screen's kept-complaint
 *  cards show the category as the card subtitle, Flutter parity). Read-only projection of the
 *  SAME document `complaintsToSelected` reads — no extra fetch. */
export function complaintCategories(
  model: PatientComplaintModel | undefined,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const entry of model?.complaints ?? []) {
    if (!isComplaintEntry(entry)) continue;
    const display = entry.coding?.display?.trim();
    const category = entry.complaintCategory?.trim();
    if (display && category && !out.has(display)) out.set(display, category);
  }
  return out;
}

/**
 * The full replace-all POST body for a new chip list.
 * - kept complaints: the EXISTING entry verbatim (patientHpi preserved), templateItem stripped;
 * - added complaints: fresh ChiefComplaint entries, category from the RAG side-map when the
 *   pick came from a suggestion (Flutter parity: free-typed entries carry no category);
 * - removed complaints: simply absent (the handler soft-deletes everything not re-posted);
 * - the GlobalNotes comment fields round-trip from the existing document.
 */
export function buildPatientComplaint(args: {
  encounterId: string;
  patientId: string;
  selected: string[];
  categoryByName?: ReadonlyMap<string, string>;
  existing?: PatientComplaintModel;
  /** Overwrite the document's GlobalNotes comment (the HPI section note — Flutter's
   *  CreateChiefCompaintEvent(comment:, notePlainText:)). `comment` carries the RICH value
   *  (a Quill delta JSON string, the shape Flutter reads) and `notePlainText` its flattened
   *  text. Omitted → the existing note round-trips untouched, exactly as before. */
  comment?: string;
  notePlainText?: string;
}): PatientComplaintModel {
  const { encounterId, patientId, selected, categoryByName, existing, comment, notePlainText } = args;
  const existingByDisplay = new Map<string, ComplaintEntry>();
  for (const entry of existing?.complaints ?? []) {
    const display = entry.coding?.display?.trim();
    if (display && isComplaintEntry(entry)) existingByDisplay.set(display, entry);
  }

  const complaints: ComplaintEntry[] = selected.map((display) => {
    const kept = existingByDisplay.get(display);
    if (kept) {
      // Verbatim round-trip minus the read-side template payload.
      const rest: ComplaintEntry = { ...kept, patientHpi: kept.patientHpi ?? [] };
      delete rest.templateItem;
      return rest;
    }
    return {
      type: CHIEF_COMPLAINT_TYPE,
      coding: { code: '', display, system: '' },
      complaintCategory: categoryByName?.get(display) ?? null,
      patientHpi: [],
    };
  });

  return {
    encounter: encounterId,
    subject: patientId,
    // Flutter posts the Quill delta in `comment` and its flattened text in `notePlainText`;
    // the rich note dialog produces exactly that pair (lib/note-delta.ts serializeNote, fed by
    // Quill's own getContents()/getText()).
    comment: comment ?? existing?.comment ?? null,
    notePlainText: notePlainText ?? (comment !== undefined ? comment : existing?.notePlainText) ?? null,
    complaints,
  };
}
